import { atom } from "jotai";
import type { Plugin } from "prosemirror-state";

import { parsePage } from "@moment/api-collab/markdown";
import { Logger } from "@moment/logging";

import { $accessTokenFunc } from "../authenication";
import { $docsApi } from "../docsapi";
import { $loadEditorState, $setEditorStateError } from "../prosemirror/editor-state.atom";
import { PmRemoteLoadError } from "../prosemirror/errors";

const logger = new Logger("load-template-editor-state");

const TEMPLATE_WORKSPACE = "moment-templates";

export interface LoadTemplateEditorStateInput {
	key: string;
	templateId: string;
	pageId: string;
	plugins: Plugin[];
}

/**
 * Helper to create a timeout promise for debugging hanging requests.
 * Clears the timeout when the main promise settles to avoid unnecessary timer callbacks.
 */
const withTimeout = <T>(promise: Promise<T>, ms: number, label: string): Promise<T> => {
	let timeoutId: ReturnType<typeof setTimeout> | undefined;

	const timeoutPromise = new Promise<never>((_, reject) => {
		timeoutId = setTimeout(() => {
			logger.warn(`Request timed out after ${ms}ms`, { label });
			reject(new Error(`Timeout: ${label} took longer than ${ms}ms`));
		}, ms);
	});

	return Promise.race([promise, timeoutPromise]).finally(() => {
		if (timeoutId !== undefined) {
			clearTimeout(timeoutId);
		}
	});
};

/**
 * Loads an editor state for a template page from the DocsAPI.
 * Templates are loaded in read-only SSR mode.
 */
export const $loadTemplateEditorState = atom(
	null,
	async (get, set, input: LoadTemplateEditorStateInput) => {
		const { key, templateId, pageId, plugins } = input;

		logger.info("Loading template editor state", { key, templateId, pageId });

		try {
			const { getAccessTokenSilently } = get($accessTokenFunc);
			const token = await getAccessTokenSilently?.();
			logger.debug("Got access token", { hasToken: !!token });

			// Get docsApi
			const docsApi = get($docsApi);
			logger.debug("Got docsApi", { hasApi: !!docsApi });

			// Fetch raw markdown from template with timeout for debugging
			const requestParams = {
				workspaceName: TEMPLATE_WORKSPACE,
				documentId: templateId,
				pageId,
				ref: "main",
			};
			logger.debug("Fetching raw markdown from DocsAPI", requestParams);

			const rawMarkdown = await withTimeout(
				docsApi.document.page.readRaw(requestParams, token),
				30000,
				`readRaw(${TEMPLATE_WORKSPACE}/${templateId}/pages/${pageId})`
			);
			logger.debug("Got raw markdown", { length: rawMarkdown.length });

			// Parse to ProseMirror doc (handle empty content)
			const markdown = rawMarkdown.trim().length === 0 ? "<br />" : rawMarkdown;
			const { doc } = parsePage(markdown);
			logger.debug("Parsed markdown to doc", { docType: doc.type });

			// Load with ssr: true (read-only, no persistence)
			set($loadEditorState, { key, doc, plugins, ssr: true, isReadOnly: true });
			logger.info("Loaded template editor state successfully", { templateId, pageId });
		} catch (err) {
			const error = new PmRemoteLoadError(err);
			logger.error("Failed to load template editor state", {
				templateId,
				pageId,
				err,
				errorMessage: err instanceof Error ? err.message : String(err),
			});
			set($setEditorStateError, { key, error });
		}
	}
);
$loadTemplateEditorState.debugLabel = "loadTemplateEditorState";
